import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  Box,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress
} from '@mui/material';
import axios from 'axios';

function QuestionDisplay({ session, onEndSession }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [reasoningOptions, setReasoningOptions] = useState([]);
  const [showReasoning, setShowReasoning] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responseStartTime, setResponseStartTime] = useState(null);

  useEffect(() => {
    fetchNextQuestion();
  }, []);

  const fetchNextQuestion = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      const apiUrl = `${baseUrl}/api/question/next`;
      const response = await axios.post(apiUrl, {
        sessionId: session.sessionId
      });

      if (response.data.success) {
        setQuestion(response.data.question);
        setSelectedAnswer('');
        setShowReasoning(false);
        setFeedback(null);
        setResponseStartTime(Date.now());
      }
    } catch (error) {
      console.error('Error fetching question:', error);
      setFeedback({ type: 'error', message: 'Failed to load question. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answerId) => {
    setSelectedAnswer(answerId);
    setShowReasoning(false);
    setFeedback(null);
  };

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer) {
      setFeedback({ type: 'warning', message: 'Please select an answer.' });
      return;
    }

    try {
      setSubmitting(true);
      const responseTime = Date.now() - responseStartTime;
      const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      const apiUrl = `${baseUrl}/api/question/answer`;

      const response = await axios.post(apiUrl, {
        sessionId: session.sessionId,
        questionId: question.id,
        selectedAnswer,
        responseTime
      });

      if (response.data.success) {
        const isCorrect = response.data.isCorrect;

        setFeedback({
          type: isCorrect ? 'success' : 'info',
          message: isCorrect
            ? 'Great job! Your answer is correct.'
            : `Not quite right. The correct answer is: ${response.data.correctAnswer}`
        });

        if (!isCorrect) {
          setReasoningOptions(response.data.reasoningOptions);
          setShowReasoning(true);
        }

        // Auto-advance after correct answer or after reasoning selection
        setTimeout(() => {
          if (isCorrect) {
            fetchNextQuestion();
          }
        }, isCorrect ? 2000 : 100);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      setFeedback({ type: 'error', message: 'Failed to submit answer. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReasoningSelect = async (reasoningOption) => {
    try {
      setSubmitting(true);
      const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      const apiUrl = `${baseUrl}/api/question/answer`;

      // Submit reasoning selection
      await axios.post(apiUrl, {
        sessionId: session.sessionId,
        questionId: question.id,
        selectedAnswer,
        reasoningOption,
        responseTime: Date.now() - responseStartTime
      });

      setFeedback({
        type: 'info',
        message: 'Thank you for sharing your thinking. Let\'s try the next question!'
      });

      setTimeout(() => {
        fetchNextQuestion();
      }, 2000);
    } catch (error) {
      console.error('Error submitting reasoning:', error);
      setFeedback({ type: 'error', message: 'Failed to submit reasoning. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const viewProgress = () => {
    navigate('/progress');
  };

  if (loading) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!question) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Alert severity="error">No question available.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" style={{ marginTop: '2rem' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="2rem">
        <Typography variant="h4" component="h1">
          Chapter {question.chapter}: {question.topic}
        </Typography>
        <Button variant="outlined" onClick={viewProgress}>
          View Progress
        </Button>
      </Box>

      {/* Chapter Progress */}
      <Box marginBottom="2rem">
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Chapter {question.chapter} Progress
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(session.currentChapter - 1) * 50}
          style={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Question Card */}
      <Card elevation={3} style={{ marginBottom: '2rem' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Question
          </Typography>
          <Typography variant="body1" paragraph style={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
            {question.text}
          </Typography>

          {/* KBAT Skills */}
          <Box marginTop="1rem">
            <Typography variant="body2" color="textSecondary" gutterBottom>
              This question practices:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap="0.5rem">
              {question.kbatsSkills.map((skill, index) => (
                <Chip
                  key={index}
                  label={skill}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Answer Options */}
      <Card elevation={2} style={{ marginBottom: '2rem' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choose your answer:
          </Typography>
          <ButtonGroup orientation="vertical" fullWidth>
            {question.options.map((option) => (
              <Button
                key={option.id}
                variant={selectedAnswer === option.id ? "contained" : "outlined"}
                color={selectedAnswer === option.id ? "primary" : "secondary"}
                onClick={() => handleAnswerSelect(option.id)}
                style={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  marginBottom: '0.5rem',
                  padding: '1rem'
                }}
                disabled={showReasoning}
              >
                <span style={{ marginRight: '1rem' }}>
                  {option.id}.
                </span>
                {option.text}
              </Button>
            ))}
          </ButtonGroup>
        </CardContent>
      </Card>

      {/* Feedback */}
      {feedback && (
        <Alert
          severity={feedback.type}
          style={{ marginBottom: '2rem' }}
        >
          {feedback.message}
        </Alert>
      )}

      {/* Reasoning Options (shown when answer is wrong) */}
      {showReasoning && reasoningOptions.length > 0 && (
        <Card elevation={2} style={{ marginBottom: '2rem' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Why did you choose this answer?
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              This helps us understand your thinking and provide better questions for you.
            </Typography>
            <ButtonGroup orientation="vertical" fullWidth>
              {reasoningOptions.map((option, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  onClick={() => handleReasoningSelect(option)}
                  style={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    marginBottom: '0.5rem',
                    padding: '1rem'
                  }}
                  disabled={submitting}
                >
                  {option}
                </Button>
              ))}
            </ButtonGroup>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" gap="1rem">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitAnswer}
          disabled={!selectedAnswer || submitting || showReasoning}
          size="large"
        >
          {submitting ? 'Submitting...' : 'Submit Answer'}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={onEndSession}
          size="large"
        >
          End Session
        </Button>
      </Box>
    </Container>
  );
}

export default QuestionDisplay;